import React from 'react';
import { Box, Typography, Grid, Paper, Tab, Tabs } from '@mui/material';
import ChartCard from '../components/Dashboard/ChartCard';
import { mockTimeSeriesData } from '../data/mockData';
import { Line } from 'react-chartjs-2';

// Convert mock data to Chart.js format
const prepareTimeSeriesData = () => {
  const allDates = [
    ...mockTimeSeriesData.historical.map(item => item.date),
    ...mockTimeSeriesData.forecast.map(item => item.date)
  ];
  
  const historicalValues = allDates.map(date => {
    const dataPoint = mockTimeSeriesData.historical.find(item => item.date === date);
    return dataPoint ? dataPoint.value : null;
  });
  
  const forecastValues = allDates.map(date => {
    const dataPoint = mockTimeSeriesData.forecast.find(item => item.date === date);
    return dataPoint ? dataPoint.value : null;
  });
  
  return {
    labels: allDates,
    datasets: [
      {
        label: 'Historical',
        data: historicalValues,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        pointRadius: historicalValues.map(v => v !== null ? 3 : 0),
      },
      {
        label: 'Forecast',
        data: forecastValues,
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderDash: [5, 5],
        pointRadius: forecastValues.map(v => v !== null ? 3 : 0),
      }
    ]
  };
};

const TimeSeriesPage: React.FC = () => {
  const [tabValue, setTabValue] = React.useState(0);
  const timeSeriesData = prepareTimeSeriesData();
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Time Series Forecasting
      </Typography>
      
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Revenue Forecast" />
          <Tab label="Cash Flow Forecast" />
          <Tab label="Expense Forecast" />
        </Tabs>
      </Paper>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              {tabValue === 0 && "Revenue Forecast"}
              {tabValue === 1 && "Cash Flow Forecast"}
              {tabValue === 2 && "Expense Forecast"}
            </Typography>
            <Box sx={{ height: 400, mb: 4 }}>
              <Line 
                data={timeSeriesData} 
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: false,
                      title: {
                        display: true,
                        text: 'Amount ($)'
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Date'
                      }
                    }
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        title: function(tooltipItems) {
                          const date = new Date(tooltipItems[0].label);
                          return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
                        }
                      }
                    },
                    legend: {
                      position: 'top',
                    },
                  }
                }}
              />
            </Box>
            
            <Typography variant="h6" gutterBottom>
              Forecast Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Forecasting Method
                  </Typography>
                  <Typography variant="body1">
                    Exponential Smoothing
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Forecast Accuracy
                  </Typography>
                  <Typography variant="body1">
                    MAPE: 3.2%
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Forecast Horizon
                  </Typography>
                  <Typography variant="body1">
                    3 Months
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TimeSeriesPage;
